// client/src/components/profissionais/ProfissionalManagementDrawer.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAccountUser } from '../../hooks/useAccounts';
import { useAuth } from '../../context/auth/AuthProvider'; 
import {
    ArrowLeft,
    Loader2, AlertCircle, XCircle
} from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import { FormStepper } from '../shared/FormStepper';

// Importação das seções (Tabs)
import UnitsSection from '../unidades/sections/UnitsSection';
import ServicesSection from './sections/ServicesSection';
import ProfissionalProfileSection from './sections/ProfissionalProfileSection';
import ProfissionalAgendaSection from './sections/ProfissionalAgendaSection';
import HorariosTab from './tabs/HorariosTab'; // ✅ NOVA ABA IMPORTADA

const safeId = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (data._id) return String(data._id);
    if (data.id) return String(data.id);
    return '';
};

const ProfissionalManagementDrawer = ({ contextData }) => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setAlert } = useAlert();
    const { user } = useAuth(); 
    
    const [activeTab, setActiveTab] = useState('profile');
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null);

    // Capturar a tab da URL
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        if (tab && ['profile', 'agenda', 'horarios', 'units', 'services'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    const targetAccountId = useMemo(() => {
        if (contextData?.contaId) return safeId(contextData.contaId);
        if (location.state?.contaId) return safeId(location.state.contaId);
        if (user) return safeId(user.account?._id || user.account);
        return null;
    }, [contextData?.contaId, location.state?.contaId, user]);

    const { 
        data: accountUserResponse, 
        isLoading: isLoadingUser,
        refetch: refetchUser,
        error: userError
    } = useAccountUser(userId, {
        enabled: !!userId,
        retry: 1
    });

    useEffect(() => {
        if (isLoadingUser) return;

        if (userError) {
            setLoadingError('Erro ao carregar dados do profissional');
            setIsLoading(false);
            return;
        }

        if (accountUserResponse) {
            let userObj = null;
            if (accountUserResponse.user) {
                userObj = accountUserResponse.user;
            } else if (accountUserResponse.data) {
                userObj = accountUserResponse.data.user || accountUserResponse.data;
            } else {
                userObj = accountUserResponse;
            }

            setUserData({
                ...userObj,
                _id: userId,
                account: targetAccountId,
                relationshipId: accountUserResponse._id,
                profissionalId: userObj.profissionalId
            });
            setIsLoading(false);
        }
    }, [accountUserResponse, isLoadingUser, userError, userId, targetAccountId]);

    const handleRefresh = useCallback(() => {
        refetchUser();
    }, [refetchUser]);

    if (isLoading) {
        return (
            <div className="page-management-container">
                <div className="table-loading-state py-20 flex flex-col items-center">
                    <Loader2 size={32} className="animate-spin text-primary mb-4" />
                    <span className="text-gray-500 font-medium">Carregando dados do profissional...</span>
                </div>
            </div>
        );
    }

    if (loadingError || !userData) {
        return (
            <div className="page-management-container">
                <div className="content-panel">
                    <div className="text-center py-12">
                        <AlertCircle className="text-red-500 mb-4 mx-auto" size={48} />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar</h3>
                        <p className="text-gray-600 mb-4">{loadingError || 'Profissional não encontrado'}</p>
                        <button 
                            onClick={() => navigate('/minha-conta/profissionais')} 
                            className="action-button action-button-secondary"
                        >
                            <ArrowLeft size={16} className="mr-2" /> Voltar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Array de abas para construir o cabeçalho de passos
    const tabsList = [
        { id: 'profile', label: 'Perfil' },
        { id: 'agenda', label: 'Agenda' },
        { id: 'horarios', label: 'Horários' },
        { id: 'units', label: 'Unidades' },
        { id: 'services', label: 'Serviços' }
    ];
    const stepLabels = tabsList.map(tab => tab.label);
    const currentStep = Math.max(1, tabsList.findIndex(tab => tab.id === activeTab) + 1);
    const goToTab = (stepNumber) => {
        const tab = tabsList[stepNumber - 1];
        if (!tab) return;
        setActiveTab(tab.id);
        navigate(`/minha-conta/profissionais/${userId}/editar?tab=${tab.id}`, { replace: true });
    };

    // RENDERIZAÇÃO CONDICIONAL DAS ABAS
    const renderSectionContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <ProfissionalProfileSection 
                        user={userData} 
                        contextData={{ contaId: targetAccountId }}
                        onUpdateSuccess={handleRefresh}
                    />
                );
            case 'agenda': 
                return (
                    <ProfissionalAgendaSection 
                        user={userData} 
                        contextData={{ contaId: targetAccountId }}
                        onUpdateSuccess={handleRefresh}
                    />
                );
            case 'horarios': // ✅ NOVA ABA RENDERIZADA
                return (
                    <HorariosTab 
                        user={userData} 
                        contextData={{ contaId: targetAccountId }}
                    />
                );
            case 'units':
                return (
                    <UnitsSection 
                        user={userData} 
                        contextData={{ contaId: targetAccountId }}
                    />
                );
            case 'services':
                return (
                    <ServicesSection 
                        user={userData} 
                        contextData={{ contaId: targetAccountId }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="page-management-container">
            {/* CABEÇALHO GLOBAL LIMPO */}
            <div className="page-header border-b pb-4 mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between w-full gap-4">
                    <div className="flex items-start md:items-center gap-4 flex-1 min-w-0">
                        <button 
                            onClick={() => navigate('/minha-conta/profissionais')} 
                            className="action-button action-button-secondary text-sm shrink-0 mt-1 md:mt-0"
                        >
                            <ArrowLeft size={16} className="mr-2" />
                            Voltar
                        </button>
                        
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="relative shrink-0">
                                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 border-2 border-white shadow-sm">
                                    <img 
                                        src={userData.photoUrl || userData.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=random`} 
                                        alt={userData.name} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=random`;
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h1 className="page-title mb-0 text-xl font-bold text-gray-800 truncate" title={userData?.name}>
                                        {userData?.name || 'Profissional'}
                                    </h1>
                                    <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                        Profissional
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                    {userData.email}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* NAVEGAÇÃO DE ABAS */}
            <FormStepper currentStep={currentStep} steps={stepLabels} onStepClick={goToTab} />

            {/* CONTEÚDO DA ABA SELECIONADA E BOTÕES DE AÇÃO NA BASE (RODAPÉ) */}
            <div className="content-panel bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 min-h-[400px]">
                    {renderSectionContent()}
                </div>

                <div className="form-actions mt-0 p-6 border-t bg-gray-50 flex justify-between rounded-b-lg">
                    <button type="button" onClick={() => navigate('/minha-conta/profissionais')} className="action-button action-button-secondary">
                        <XCircle size={18} className="mr-2" /> Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfissionalManagementDrawer;